import Button from "@/components/common/Button";
import FloatingFAQButton from "@/components/common/FloatingFAQButton";
import PhoneNumberInput from "@/components/common/PhoneNumberInput";
import { useRegisterUser } from "@/hooks/queries/useAuthQueries";
import { zodResolver } from "@hookform/resolvers/zod";

import { motion } from "framer-motion";
import { Building2, Eye, EyeOff, Mail, Shield, User } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

type FormValues = {
  fullName: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
};

interface Company {
  id: string;
  name: string;
  departments?: Department[];
}

interface Department {
  id: string;
  name: string;
}

// Function to decode JWT token and extract email
const decodeToken = (
  token: string
): { email: string; companyId: string; departmentId: string } | null => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    const payload = JSON.parse(jsonPayload);
    return {
      email: payload.email,
      companyId: payload.companyId,
      departmentId: payload.departmentId,
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token") || "";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const registerUser = useRegisterUser();

  const [isLoading, setIsLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  // Decode token to get email for display purposes
  const tokenData = token ? decodeToken(token) : null;
  const email = tokenData?.email;

  // Create schema with translated messages
  const createSchema = () =>
    z
      .object({
        fullName: z
          .string()
          .min(2, t("auth.fullNameRequired", "Full name is required")),
        phoneNumber: z
          .string()
          .min(1, t("auth.phoneNumberRequired", "Phone number is required"))
          .regex(
            /^\+[1-9]\d{1,14}$/,
            t(
              "auth.invalidPhoneNumber",
              "Phone number must be in international format (e.g., +251912345678)"
            )
          ),
        password: z
          .string()
          .min(
            8,
            t(
              "auth.passwordMinLength",
              "Password must be at least 8 characters"
            )
          )
          .regex(
            /[A-Z]/,
            t(
              "auth.passwordUppercase",
              "Must contain at least one uppercase letter"
            )
          )
          .regex(
            /[a-z]/,
            t(
              "auth.passwordLowercase",
              "Must contain at least one lowercase letter"
            )
          )
          .regex(
            /[0-9]/,
            t("auth.passwordNumber", "Must contain at least one number")
          )
          .regex(
            /[^A-Za-z0-9]/,
            t(
              "auth.passwordSpecial",
              "Must contain at least one special character"
            )
          ),
        passwordConfirm: z.string(),
      })
      .refine((data) => data.password === data.passwordConfirm, {
        message: t("auth.passwordsDontMatch", "Passwords don't match"),
        path: ["passwordConfirm"],
      });

  const schema = createSchema();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);

      const registrationData = {
        fullName: data.fullName,
        phoneNumber: data.phoneNumber,
        password: data.password,
        passwordConfirm: data.passwordConfirm,
        token: token,
      };

      await registerUser.mutateAsync({ userData: registrationData, token });

      setRegisteredEmail(email || "");
      setVerificationSent(true);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message || err?.message;
      const statusCode = err?.response?.status;

      if (statusCode === 409) {
        // Check which field is already taken
        const errorMessage = err?.response?.data?.message || "";
        if (errorMessage.includes("email")) {
          toast.error(
            t(
              "auth.emailAlreadyExists",
              "Email already exists. Please use a different email."
            )
          );
        } else if (
          errorMessage.includes("phoneNumber") ||
          errorMessage.includes("+251")
        ) {
          toast.error(
            t(
              "auth.phoneAlreadyExists",
              "Phone number already exists. Please use a different phone number."
            )
          );
        } else if (errorMessage.includes("fullName")) {
          toast.error(
            t(
              "auth.nameAlreadyExists",
              "Name already exists. Please use a different name."
            )
          );
        } else {
          toast.error(
            t(
              "auth.fieldAlreadyExists",
              "This information is already registered. Please use different values."
            )
          );
        }
      } else {
        toast.error(
          backendMsg || t("auth.registrationFailed", "Registration failed.")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md rounded-3xl bg-white/80 dark:bg-gray-800/90 p-8 shadow-2xl border border-white/20 dark:border-gray-700/50 backdrop-blur-xl flex flex-col items-center"
        >
          <div className="flex flex-col items-center">
            <div className="mb-6 flex items-center justify-center rounded-full bg-gradient-to-r from-primary-400 to-primary-600 p-5 shadow-lg">
              <svg
                className="h-14 w-14 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 text-center">
              {t("auth.verificationEmailSent")}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-2 text-center text-lg">
              {t("auth.checkYourEmail")}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 text-center">
              {t("auth.verificationEmailSentTo", { email: registeredEmail })}
            </p>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              variant="primary"
              size="lg"
            >
              {t("auth.backToLogin")}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show error if no valid token
  if (!token || !tokenData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 via-pink-50 to-red-100 dark:from-gray-900 dark:via-red-900/20 dark:to-gray-900">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 space-y-6 border border-red-200/50 dark:border-red-700/50 backdrop-blur-xl"
        >
          <div className="text-center">
            <div className="mb-6 flex items-center justify-center rounded-full bg-gradient-to-r from-red-400 to-pink-500 p-5 shadow-lg">
              <svg
                className="h-14 w-14 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t("auth.invalidInvitation", "Invalid Invitation")}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg leading-relaxed">
              {t(
                "auth.invalidInvitationMessage",
                "This invitation link is invalid or has expired. Please ask your company admin for a new invitation."
              )}
            </p>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              variant="primary"
              size="lg"
            >
              {t("auth.backToLogin", "Back to Login")}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-green-50 to-emerald-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4 lg:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8"
        >
          {/* Header Section */}
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex justify-center">
              <div className="p-2 sm:p-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl shadow-lg">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary-800 to-primary-700 dark:from-white dark:via-primary-200 dark:to-primary-300 bg-clip-text text-transparent">
              {t("auth.register")}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300">
              {t("auth.registerDescription")}
            </p>

            {/* Email Display Card */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-primary-50 to-primary-100/50 dark:from-primary-900/30 dark:to-primary-800/30 rounded-xl sm:rounded-2xl border border-primary-200/50 dark:border-primary-700/50">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                <p className="text-xs sm:text-sm font-medium text-primary-800 dark:text-primary-200">
                  {t("auth.registeringForEmail", "Registering for:")}
                </p>
              </div>
              <p className="text-primary-900 dark:text-primary-100 font-semibold text-base sm:text-lg break-words">
                {email}
              </p>
            </div>
          </div>

          {/* Form Section */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            <div className="space-y-4 sm:space-y-5">
              {/* Full Name Input */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <User className="h-4 w-4 text-primary-600" />
                  <span>{t("auth.fullName")}</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  required
                  placeholder={t("auth.fullName")}
                  className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                  {...register("fullName", { required: true })}
                />
                {errors.fullName && (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    <span>{errors.fullName.message}</span>
                  </p>
                )}
              </div>

              {/* Phone Number Input */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Building2 className="h-4 w-4 text-primary-600" />
                  <span>{t("auth.phoneNumber")}</span>
                </label>
                <PhoneNumberInput
                  value={watch("phoneNumber") || ""}
                  onChange={(value) => setValue("phoneNumber", value || "")}
                  error={errors.phoneNumber?.message}
                  placeholder={t("auth.phoneNumber")}
                  required
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Shield className="h-4 w-4 text-primary-600" />
                  <span>{t("auth.password")}</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    placeholder={t("auth.password")}
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 pr-12 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                      errors.password
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
                    } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                    {...register("password", { required: true })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    <span>{errors.password.message}</span>
                  </p>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Shield className="h-4 w-4 text-primary-600" />
                  <span>{t("auth.confirmPassword")}</span>
                </label>
                <div className="relative">
                  <input
                    id="passwordConfirm"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    placeholder={t("auth.confirmPassword")}
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 pr-12 rounded-lg sm:rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 ${
                      errors.passwordConfirm
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-200 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
                    } focus:ring-4 outline-none transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm hover:shadow-md focus:shadow-lg text-sm sm:text-base`}
                    {...register("passwordConfirm", { required: true })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
                {errors.passwordConfirm && (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    <span>{errors.passwordConfirm.message}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 sm:mt-8 py-3 sm:py-4 rounded-lg sm:rounded-xl bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white font-bold shadow-lg hover:from-primary-700 hover:via-primary-800 hover:to-primary-900 focus:ring-4 focus:ring-primary-500/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 transition-all duration-300 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t("common.loading", "Loading...")}</span>
                </div>
              ) : (
                t("auth.register")
              )}
            </button>

            {/* Links Section */}
            <div className="space-y-3 sm:space-y-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="text-center text-sm sm:text-base text-gray-600 dark:text-gray-300">
                {t("auth.alreadyHaveAccount")}{" "}
                <Link
                  to="/login"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold hover:underline transition-colors duration-200"
                >
                  {t("auth.login")}
                </Link>
              </div>
              <div className="text-center text-sm sm:text-base text-gray-600 dark:text-gray-300">
                {t("auth.registerAsCompany")}{" "}
                <Link
                  to="/register-company"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold hover:underline transition-colors duration-200"
                >
                  {t("auth.registerCompany")}
                </Link>
              </div>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Floating FAQ Button */}
      <FloatingFAQButton />
    </div>
  );
};

export default Register;
